import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, DollarSign, FileText, CheckCircle, Clock, Truck, ChevronLeft, ChevronRight } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line } from 'recharts';

const ACCENT_EMERALD = '#10b981';
const ACCENT_BLUE = '#3b82f6';

const localeForCalendar = (lang) => lang === 'es' ? 'es-ES' : 'en-GB';

// Calendar Component
const ServiceCalendar = ({ orders, onServiceClick }) => {
  const { t, i18n } = useTranslation();
  const locale = localeForCalendar(i18n.language);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());

  const monthNames = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(2000, i, 1);
    return d.toLocaleDateString(locale, { month: 'long' });
  });

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getOrdersForDate = (date) => {
    return orders.filter(order => {
      if (!order.service_date) return false;
      const orderDate = new Date(order.service_date);
      return orderDate.getDate() === date.getDate() &&
             orderDate.getMonth() === date.getMonth() &&
             orderDate.getFullYear() === date.getFullYear();
    });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const previousPeriod = () => {
    if (viewMode === 'day') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() - 1);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else if (viewMode === 'week') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() - 7);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else {
      previousMonth();
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'day') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else if (viewMode === 'week') {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 7);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else {
      nextMonth();
    }
  };

  const getWeekDates = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const monday = new Date(start.setDate(diff));
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const getPeriodLabel = () => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString(locale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else if (viewMode === 'week') {
      const weekDates = getWeekDates(selectedDate);
      const start = weekDates[0].toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      const end = weekDates[6].toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
      return `${start} - ${end}`;
    } else {
      return `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    }
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const today = new Date();

  // Create calendar grid
  const calendarDays = [];
  
  // Add empty cells for days before the first day of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="min-h-[44px] bg-slate-50/80" />);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOrders = getOrdersForDate(date);
    const isToday = date.toDateString() === today.toDateString();

    calendarDays.push(
      <div 
        key={day} 
        className={`min-h-[44px] border border-slate-100 p-1 relative rounded-md ${isToday ? 'bg-sky-50 ring-1 ring-sky-200' : 'bg-white hover:bg-slate-50/80'}`}
      >
        <div className={`text-[10px] font-semibold ${isToday ? 'text-sky-700' : 'text-slate-700'}`}>
          {day}
        </div>
        <div className="space-y-0.5">
          {dayOrders.map((order, idx) => (
            <div key={idx} className="relative group">
              <div 
                className={`text-[8px] px-0.5 py-0.5 rounded truncate cursor-pointer transition-all hover:scale-105 ${
                  order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                  order.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                  'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onMouseEnter={() => setHoveredOrder(order.id)}
                onMouseLeave={() => setHoveredOrder(null)}
                onClick={() => onServiceClick && onServiceClick()}
              >
                {order.service_time}
              </div>
              
              {/* Tooltip on hover - positioned relative to this cell */}
              {hoveredOrder === order.id && (
                <div 
                  className="absolute z-[9999] bg-gray-900 text-white text-[10px] p-3 rounded-lg shadow-2xl border border-gray-700 left-full ml-2 top-0 whitespace-normal"
                  style={{
                    minWidth: '220px',
                    maxWidth: '280px'
                  }}
                  onMouseEnter={() => setHoveredOrder(order.id)}
                  onMouseLeave={() => setHoveredOrder(null)}
                >
                  <div className="space-y-2">
                    <div>
                      <div className="font-bold text-green-400 mb-1 flex items-center gap-1">
                        <span>📍</span> {t('calendar_origin')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.pickup_address}</div>
                    </div>
                    <div className="border-t border-gray-700 pt-2">
                      <div className="font-bold text-blue-400 mb-1 flex items-center gap-1">
                        <span>🎯</span> {t('calendar_destination')}
                      </div>
                      <div className="pl-4 text-gray-200 leading-relaxed">{order.delivery_address}</div>
                    </div>
                    <div 
                      className="text-[8px] text-center pt-2 border-t border-gray-700 cursor-pointer hover:bg-gray-800 rounded py-1 transition-colors"
                      onClick={() => {
                        setHoveredOrder(null);
                        onServiceClick && onServiceClick();
                      }}
                    >
                      <span className="text-blue-400 font-semibold">→ {t('calendar_view_full_details')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* View Mode Selector */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
        {['day', 'week', 'month'].map((mode) => (
          <Button
            key={mode}
            variant="outline"
            size="sm"
            onClick={() => setViewMode(mode)}
            className={`h-8 rounded-full px-4 text-xs font-medium transition-all ${
              viewMode === mode
                ? 'border-transparent bg-slate-900 text-white shadow-sm hover:bg-slate-900 hover:text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {mode === 'day' ? t('calendar_day') : mode === 'week' ? t('calendar_week') : t('calendar_month')}
          </Button>
        ))}
      </div>

      {/* Calendar Header */}
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={previousPeriod} className="h-9 w-9 shrink-0 rounded-full border-slate-200 p-0" aria-label={t('calendar_prev_period')}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="text-sm font-semibold text-slate-900 capitalize text-center flex-1">
          {getPeriodLabel()}
        </h3>
        <Button variant="outline" size="sm" onClick={nextPeriod} className="h-9 w-9 shrink-0 rounded-full border-slate-200 p-0" aria-label={t('calendar_next_period')}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Calendar Grid - Month View */}
      {viewMode === 'month' && (
        <div className="grid grid-cols-7 gap-0.5 overflow-visible">
          {/* Day headers */}
          {(() => {
            const days = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(2024, 0, 7 + i); // Sun 7 -> Sat 13
              days.push(d.toLocaleDateString(locale, { weekday: 'short' }));
            }
            return days.map(day => (
              <div key={day} className="text-center text-[10px] font-semibold text-slate-500 py-1">
                {day}
              </div>
            ));
          })()}
          
          {/* Calendar days */}
          {calendarDays}
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="grid grid-cols-7 gap-1">
          {(() => {
            const weekDayLabels = [];
            for (let i = 1; i <= 7; i++) {
              const d = new Date(2024, 0, i);
              weekDayLabels.push(d.toLocaleDateString(locale, { weekday: 'short' }));
            }
            return weekDayLabels;
          })().map((day, index) => {
            const weekDates = getWeekDates(selectedDate);
            const date = weekDates[index];
            const dayOrders = getOrdersForDate(date);
            const isToday = date.toDateString() === today.toDateString();

            return (
              <div key={index} className="flex flex-col">
                <div className={`text-center text-xs font-semibold py-2 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                  {day} {date.getDate()}
                </div>
                <div className={`border rounded p-2 min-h-[200px] ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'}`}>
                  <div className="space-y-1">
                    {dayOrders.map((order, idx) => (
                      <div key={idx} className="relative group">
                        <div 
                          className={`text-[9px] px-1.5 py-1 rounded cursor-pointer transition-all hover:scale-105 ${
                            order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                            order.status === 'completed' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                            'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                          onMouseEnter={() => setHoveredOrder(order.id)}
                          onMouseLeave={() => setHoveredOrder(null)}
                          onClick={() => onServiceClick && onServiceClick()}
                        >
                          <div className="font-semibold">{order.service_time}</div>
                          <div className="text-[8px] truncate">{order.pickup_address?.split(',')[0]}</div>
                        </div>
                        
                        {hoveredOrder === order.id && (
                          <div 
                            className="absolute z-[9999] bg-gray-900 text-white text-[10px] p-3 rounded-lg shadow-2xl border border-gray-700 left-full ml-2 top-0 whitespace-normal"
                            style={{ minWidth: '220px', maxWidth: '280px' }}
                            onMouseEnter={() => setHoveredOrder(order.id)}
                            onMouseLeave={() => setHoveredOrder(null)}
                          >
                            <div className="space-y-2">
                              <div>
                                <div className="font-bold text-green-400 mb-1 flex items-center gap-1">
                                  <span>📍</span> {t('calendar_origin')}
                                </div>
                                <div className="pl-4 text-gray-200 leading-relaxed">{order.pickup_address}</div>
                              </div>
                              <div className="border-t border-gray-700 pt-2">
                                <div className="font-bold text-blue-400 mb-1 flex items-center gap-1">
                                  <span>🎯</span> {t('calendar_destination')}
                                </div>
                                <div className="pl-4 text-gray-200 leading-relaxed">{order.delivery_address}</div>
                              </div>
                              <div 
                                className="text-[8px] text-center pt-2 border-t border-gray-700 cursor-pointer hover:bg-gray-800 rounded py-1 transition-colors"
                                onClick={() => {
                                  setHoveredOrder(null);
                                  onServiceClick && onServiceClick();
                                }}
                              >
                                <span className="text-blue-400 font-semibold">→ {t('calendar_view_full_details')}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {dayOrders.length === 0 && (
                      <div className="text-[8px] text-gray-400 text-center py-4">{t('calendar_no_services')}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="border rounded p-4 bg-white">
          <div className="space-y-2">
            {getOrdersForDate(selectedDate).map((order, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
                  order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-50 border-2 border-yellow-200' :
                  order.status === 'completed' ? 'bg-green-50 border-2 border-green-200' :
                  'bg-gray-50 border-2 border-gray-200'
                }`}
                onClick={() => onServiceClick && onServiceClick()}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-bold">{order.service_time}</div>
                  <div className={`text-[9px] px-2 py-1 rounded font-semibold ${
                    order.status === 'assigned' || order.status === 'in_progress' ? 'bg-yellow-200 text-yellow-800' :
                    order.status === 'completed' ? 'bg-green-200 text-green-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {order.status === 'assigned' ? t('calendar_legend_assigned') : order.status === 'completed' ? t('calendar_legend_completed') : order.status}
                  </div>
                </div>
                <div className="space-y-1.5 text-[10px]">
                  <div>
                    <span className="font-semibold text-green-600">📍 {t('calendar_origin')}</span>
                    <div className="pl-4 text-gray-700">{order.pickup_address}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">🎯 {t('calendar_destination')}</span>
                    <div className="pl-4 text-gray-700">{order.delivery_address}</div>
                  </div>
                </div>
              </div>
            ))}
            {getOrdersForDate(selectedDate).length === 0 && (
              <div className="text-center text-gray-400 py-8">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>{t('calendar_no_services_this_day')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600 justify-center pt-1">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-amber-400" />
          <span>{t('calendar_legend_assigned')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span>{t('calendar_legend_completed')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-sky-400" />
          <span>{t('calendar_legend_today')}</span>
        </div>
      </div>
    </div>
  );
};

const DriverDashboardHome = ({ driver, orders, documents, onTabChange, hideOrderPrices = false }) => {
  const { t, i18n } = useTranslation();
  const locale = localeForCalendar(i18n.language);

  // Get current week's orders (Monday to Sunday)
  const getCurrentWeekOrders = () => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to get Monday
    
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return orders.filter(order => {
      const orderDate = new Date(order.created_at || order.service_date);
      return orderDate >= monday && orderDate <= sunday;
    });
  };

  const weekOrders = getCurrentWeekOrders();

  // Calculate statistics for cards (all time)
  const stats = {
    total: orders.length,
    in_progress: orders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'in_transit' || 
      o.status === 'confirmed'
    ).length,
    completed: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
    total_earnings: hideOrderPrices
      ? 0
      : orders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total_price * 0.7 || 0), 0), // Assume 70% commission
    documents_status: {
      total: 7,
      uploaded: documents.length,
      approved: documents.filter(d => d.status === 'approved').length,
      pending: documents.filter(d => d.status === 'pending').length,
      rejected: documents.filter(d => d.status === 'rejected').length
    }
  };

  // Calculate statistics for charts (current week only)
  const weekStats = {
    in_progress: weekOrders.filter(o => 
      o.status === 'assigned' || 
      o.status === 'in_transit' || 
      o.status === 'confirmed'
    ).length,
    completed: weekOrders.filter(o => o.status === 'delivered').length,
    cancelled: weekOrders.filter(o => 
      o.status === 'cancelled' || 
      o.status?.includes('Cancelado')
    ).length,
  };

  // Weekly service counts for bar chart
  const weeklyStatusBarData = [
    { name: t('admin_status_in_progress'), count: weekStats.in_progress, fill: ACCENT_BLUE },
    { name: t('driver_completed'), count: weekStats.completed, fill: ACCENT_EMERALD },
    { name: t('driver_chart_cancelled'), count: weekStats.cancelled, fill: '#ef4444' },
  ];
  const weekChartHasData = weeklyStatusBarData.some((d) => d.count > 0);

  // Document status chart data
  const documentChartData = [
    { name: t('driver_chart_approved'), value: stats.documents_status.approved, color: ACCENT_EMERALD },
    { name: t('driver_doc_pending'), value: stats.documents_status.pending, color: '#f59e0b' },
    { name: t('driver_chart_rejected'), value: stats.documents_status.rejected, color: '#ef4444' },
    { name: t('driver_chart_missing'), value: stats.documents_status.total - stats.documents_status.uploaded, color: '#94a3b8' },
  ];

  // Prepare monthly trend data (last 6 months)
  const getMonthlyTrendData = () => {
    const monthNamesShort = Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString(locale, { month: 'short' })
    );
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    const data = [];
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(currentYear, currentMonth - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      const monthOrders = orders.filter(order => {
        const orderDate = new Date(order.created_at || order.service_date);
        return orderDate >= monthStart && orderDate <= monthEnd;
      });

      data.push({
        month: monthNamesShort[monthDate.getMonth()],
        completados: monthOrders.filter(o => o.status === 'delivered').length,
        ingresos: hideOrderPrices
          ? 0
          : monthOrders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total_price * 0.7 || 0), 0)
      });
    }
    
    return data;
  };

  const monthlyTrendData = getMonthlyTrendData();

  const statCardClass =
    'border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-shadow hover:shadow-md';

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={`${statCardClass} rounded-2xl`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-slate-600">{t('driver_total_services')}</CardTitle>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
              <Package className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="text-2xl font-bold tracking-tight text-slate-900">{stats.total}</div>
            <p className="text-xs text-slate-500 mt-1">{t('driver_completed_count', { count: stats.completed })}</p>
          </CardContent>
        </Card>

        <Card className={`${statCardClass} rounded-2xl`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-slate-600">{t('admin_status_in_progress')}</CardTitle>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
              <Clock className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="text-2xl font-bold tracking-tight text-sky-700">{stats.in_progress}</div>
            <p className="text-xs text-slate-500 mt-1">{t('driver_active_services')}</p>
          </CardContent>
        </Card>

        <Card className={`${statCardClass} rounded-2xl`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-slate-600">{t('total_revenue')}</CardTitle>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <DollarSign className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="text-2xl font-bold tracking-tight text-emerald-600">
              {hideOrderPrices ? '—' : `€${stats.total_earnings.toFixed(2)}`}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {hideOrderPrices ? t('driver_fleet_employee_no_tariffs') : t('driver_from_n_services', { count: stats.completed })}
            </p>
          </CardContent>
        </Card>

        <Card className={`${statCardClass} rounded-2xl`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
            <CardTitle className="text-sm font-medium text-slate-600">{t('admin_documents')}</CardTitle>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
              <FileText className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="text-2xl font-bold tracking-tight text-slate-900">
              {stats.documents_status.approved}/{stats.documents_status.total}
            </div>
            <p className="text-xs mt-1">
              {driver?.documents_validated ? (
                <span className="text-emerald-600 flex items-center gap-1 font-medium">
                  <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                  {t('driver_validated')}
                </span>
              ) : (
                <span className="text-amber-700 flex items-center gap-1 font-medium">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  {t('driver_doc_pending')}
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-visible rounded-2xl border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
        <CardHeader className="space-y-1">
          <CardTitle className="text-lg text-slate-900">{t('calendar_title')}</CardTitle>
          <CardDescription>{t('calendar_scheduled_subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-visible">
          <ServiceCalendar 
            orders={orders} 
            onServiceClick={() => onTabChange && onTabChange('orders')}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-lg text-slate-900">{t('driver_status_services')}</CardTitle>
            <CardDescription>{t('driver_this_week_n_services', { count: weekOrders.length })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full min-h-[260px]">
              {weekChartHasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyStatusBarData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={56}>
                      {weeklyStatusBarData.map((entry, index) => (
                        <Cell key={`bar-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 text-center">
                  <Package className="mb-2 h-10 w-10 text-slate-300" aria-hidden />
                  <p className="text-sm font-medium text-slate-600">{t('driver_chart_week_empty_title')}</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-xs">{t('driver_chart_week_empty_hint')}</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] text-slate-600">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#3b82f6]" />{t('admin_status_in_progress')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />{t('driver_completed')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-400" />{t('driver_chart_cancelled')}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-lg text-slate-900">{t('driver_status_documents')}</CardTitle>
            <CardDescription>{t('driver_validation_progress', { uploaded: stats.documents_status.uploaded })}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={documentChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={88}
                    paddingAngle={2}
                    labelLine={false}
                    label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}
                    dataKey="value"
                  >
                    {documentChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="white" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
        <CardHeader className="space-y-1">
          <CardTitle className="text-lg text-slate-900">{t('driver_trend_title')}</CardTitle>
          <CardDescription>{t('driver_trend_subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[360px] sm:h-[400px] w-full min-h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                {!hideOrderPrices && (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                )}
                <Tooltip
                  formatter={(value, name) => [
                    name === 'completados' ? `${value}` : `€${Number(value).toFixed(2)}`,
                    name === 'completados' ? t('driver_chart_completed') : t('driver_chart_income'),
                  ]}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="completados"
                  name={t('driver_chart_completed')}
                  stroke={ACCENT_EMERALD}
                  strokeWidth={2.5}
                  dot={{ fill: ACCENT_EMERALD, strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6 }}
                />
                {!hideOrderPrices && (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="ingresos"
                    name={`${t('driver_chart_income')} (€)`}
                    stroke={ACCENT_BLUE}
                    strokeWidth={2.5}
                    dot={{ fill: ACCENT_BLUE, strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {orders.length > 0 && (
        <Card className="rounded-2xl border-slate-100/80 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-lg text-slate-900">{t('driver_recent_services')}</CardTitle>
            <CardDescription>{t('driver_last_5_assigned')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4 transition-colors hover:bg-white hover:border-slate-200 hover:shadow-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Truck className="w-4 h-4 text-slate-500 shrink-0" />
                      <span className="font-semibold text-slate-900">#{order.id?.substring(0, 8) || 'N/A'}</span>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.status === 'delivered'
                            ? 'bg-emerald-100 text-emerald-800'
                            : order.status === 'in_transit'
                              ? 'bg-sky-100 text-sky-800'
                              : order.status === 'assigned'
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {order.status === 'delivered'
                          ? t('service_status_delivered')
                          : order.status === 'in_transit'
                            ? t('service_status_in_transit')
                            : order.status === 'assigned'
                              ? t('service_status_assigned')
                              : order.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                      {order.pickup_address} → {order.delivery_address}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {order.service_date ? new Date(order.service_date).toLocaleDateString(locale) : t('driver_date_unavailable')}
                    </p>
                  </div>
                  {order.status === 'delivered' && !hideOrderPrices && (
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-emerald-600">€{((order.total_price || 0) * 0.7).toFixed(2)}</p>
                      <p className="text-xs text-slate-500">{t('driver_commission')}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverDashboardHome;